import React, { ElementType, forwardRef, useMemo } from 'react';
import { Box, BoxProps } from '@mui/material';

export interface FlowFlagProps extends Omit<BoxProps, 'component'> {
  /**
   * SVG of Flag, e.g. import CZFLag from 'country-flag-icons/react/3x2/CZ';
   */
  source?: ElementType;
  /**
   * The size of the flag
   * @default small
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * The width of the flag
   */
  width?: number;
  /**
   * Variants
   * @default circle
   */
  variant?: '3x2' | 'circle' | 'square';
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
}

/**
 * Renders country flag icon
 */
export const FlowFlag: React.FC<FlowFlagProps> = forwardRef(
  ({ source, sx, width: widthProp, size = 'small', variant = 'circle', disabled = false, ...rest }, ref) => {
    // obtain width of the flag from size and width property
    const width = useMemo(() => {
      switch (size) {
        case 'small':
          return 24;
        case 'medium':
          return 44;
        case 'large':
          return 56;
        default:
          return widthProp ?? 24;
      }
    }, [size, widthProp]);

    return (
      <Box
        data-testid="Flag"
        component={source ?? 'div'}
        sx={{
          width: `${width}px`,
          flexShrink: '0',
          // outline to improve accessibility
          border: 1,
          borderColor: 'onSurface.os500',
          // 1:1 ratio
          ...(['circle', 'square'].includes(variant) && {
            height: `${width}px`,
            ...(variant === 'circle' ? { borderRadius: `${width}px` } : {}),
          }),
          // disabled state
          ...(disabled && {
            userSelect: 'none',
            borderColor: 'disabled.tertiary',
            opacity: '0.65',
          }),
          ...sx,
        }}
        ref={ref}
        {...rest}
      />
    );
  },
);
